import { Injectable } from '@angular/core';
import { ethers } from 'ethers';
import { WALLET_LOGIN } from '../common/interface/common.interface';
import { AuthService } from './auth.service';
import { STORAGE_KEYS, WALLET_PROVIDERS } from '../common/constants/common.constants';
import { createPublicClient, http, formatUnits } from 'viem';
import { polygonAmoy } from 'viem/chains';
import { environment } from '../../environments/environment';
import { readContract } from '@wagmi/core';
import { ToastService } from './toast.service';


@Injectable({
  providedIn: 'root'
})
export class Web3ServiceService {
  private provider: ethers.BrowserProvider | null = null;
  private readonly CTF_EXCHANGE_ADDRESS = environment.CTF_EXCHANGE_ADDRESS;

  constructor(private toastr: ToastService) { }

  init(providerOption: WALLET_LOGIN | null = null): ethers.BrowserProvider | null {
    if (!providerOption) {
      let option: string | null = localStorage.getItem(STORAGE_KEYS.LOGIN_TYPE);
      providerOption = option ? JSON.parse(option).provider : option;
    }
    if ((window as any).ethereum) {
      if (providerOption == WALLET_PROVIDERS.METAMASK && (window as any).ethereum?.isMetaMask) {
        this.provider = new ethers.BrowserProvider((window as any).ethereum);
      }
      // else if (providerOption == WALLET_PROVIDERS.WALLET_CONNECT)
      //   this.provider = walletConnectProviders
    }

    return this.provider
  }

  async connectWallet(): Promise<string | null> {
    if (!this.provider)
      this.init();

    let accounts = await this.provider?.send("eth_requestAccounts", []);
    return accounts;
  }

  async getCurrentAccount(): Promise<string | null> {
    if (!this.provider)
      this.init();

    const accounts = await this.provider?.send("eth_accounts", []);
    return accounts.length > 0 ? accounts[0] : null;
  }

  async getSignerAndAddress(): Promise<any> {
    // Always refresh the provider to avoid stale network state after chain switches
    await this.refreshProvider();

    let signer: ethers.JsonRpcSigner | undefined = await this.provider?.getSigner();
    let address: string | undefined = await signer?.getAddress();
    return { signer, address };
  }

  async getAccountBalance(): Promise<string | number | null> {
    if (!this.provider)
      this.init();

    let { address } = await this.getSignerAndAddress();
    const balanceWei: ethers.BigNumberish | undefined = await this.provider?.getBalance(address);
    let balance = "0";
    if (balanceWei) balance = ethers.formatEther(balanceWei);
    return balance
  }

  async createSignature(payload: { message: string; nonce: string; }): Promise<any> {
    if (!this.provider)
      this.init();

    let { signer, address } = await this.getSignerAndAddress();
    return await signer?.signMessage(payload.message)
  }

  clearState(): void {
    this.provider = null;
  }

  /**
   * Force-recreate the BrowserProvider and wait for it to detect the current network.
   * This prevents NETWORK_ERROR / BAD_DATA after the user switches chains and switches back.
   */
  private async refreshProvider(): Promise<void> {
    this.provider = null;
    this.init();

    const provider = this.provider as ethers.BrowserProvider | null;
    if (!provider) return;

    // Force the provider to query and cache the CURRENT network
    // Without this, ethers may still think it's on the old chain
    try {
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);

      // If not on Amoy, wait briefly and retry once — MetaMask may still be settling
      if (chainId !== 80002) {
        await new Promise(resolve => setTimeout(resolve, 500));
        this.provider = null;
        this.init();
        const retryProvider = this.provider as ethers.BrowserProvider | null;
        if (retryProvider) {
          await retryProvider.getNetwork();
        }
      }
    } catch (e) {
      console.warn('Network detection during provider refresh:', e);
    }
  }

  client = createPublicClient({
    chain: polygonAmoy,
    transport: http('https://rpc-amoy.polygon.technology'),
  });


  erc20Abi = [
    {
      name: 'balanceOf',
      type: 'function',
      stateMutability: 'view',
      inputs: [{ name: 'account', type: 'address' }],
      outputs: [{ type: 'uint256' }],
    },
    {
      name: 'decimals',
      type: 'function',
      stateMutability: 'view',
      inputs: [],
      outputs: [{ type: 'uint8' }],
    },

    {
      name: 'transfer',
      type: 'function',
      stateMutability: 'nonpayable',
      inputs: [
        { name: 'to', type: 'address' },
        { name: 'value', type: 'uint256' }
      ],
      outputs: [{ type: 'bool' }],
    },

  ];

  async getUSDCBalance(address: `0x${string}`) {
    try {
      // get decimals (USDC = usually 6)
      const decimals: any = await this.client.readContract({
        address: environment.USDC_ADDRESS as `0x${string}`,
        abi: this.erc20Abi,
        functionName: 'decimals',
      });

      // get raw balance
      const balance: any = await this.client.readContract({
        address: environment.USDC_ADDRESS as `0x${string}`,
        abi: this.erc20Abi,
        functionName: 'balanceOf',
        args: [address],
      });

      return formatUnits(balance, decimals);
    } catch (error) {
      console.error('USDC balance error:', error);
      return null;
    }
  }

  async transferUSDC(to: string, amount: number) {
    try {
      const { signer, address } = await this.getSignerAndAddress();
      if (!signer || !address) throw new Error("No signer found");

      // Verify we are on Polygon Amoy before making any contract calls
      const currentProvider = this.provider as ethers.BrowserProvider | null;
      if (currentProvider) {
        const network = await currentProvider.getNetwork();
        const chainId = Number(network.chainId);
        if (chainId !== 80002) {
          throw new Error(`Wrong network. Please switch to Polygon Amoy.`);
        }
      }

      const contract = new ethers.Contract(
        environment.USDC_ADDRESS,
        this.erc20Abi,
        signer
      );

      // Get decimals (USDC = 6)
      const decimals = await contract['decimals']();

      // Convert input amount → smallest unit
      const parsedAmount = ethers.parseUnits(amount.toString(), decimals);

      // Get user balance
      const balance = await contract['balanceOf'](address);

      //  Check balance
      // if (balance < parsedAmount) {
      //   this.toastr.error("Insufficient Balance");
      //   return;
      // }


      // Estimate gas
      let gasLimit;
      try {
        const estimatedGas = await contract['transfer'].estimateGas(to, parsedAmount);
        gasLimit = (estimatedGas * 120n) / 100n; // Add 20% buffer
        // console.log("Estimated gas:", estimatedGas, "With Buffer:", gasLimit);
      } catch (e) {
        console.error("Gas estimation failed, using fallback:", e);
        gasLimit = 100000n; // Fallback gas limit
      }

      // Get gas price from network
      const gasPrice = await this.provider!.send("eth_gasPrice", []);

      // Send transaction
      const tx = await contract['transfer'](to, parsedAmount, {
        gasLimit,
        gasPrice
      });

      return await tx.wait();

    } catch (error: any) {
      console.error('Transfer error:', error);
      throw error;
    }
  }

  async getCtfExchangeAbi(): Promise<any> {
    const abi = await import('../../assets/contract/CTFExchange_abi.json');
    return abi.default;
  }

  async getUserNonce(userAddress: string) {

    const chainId = await this.client.getChainId();
    // console.log("Connected Chain ID:", chainId);
    // console.log("CONTRACT:", this.CTF_EXCHANGE_ADDRESS);
    const abi = await this.getCtfExchangeAbi();

    const client = createPublicClient({
      chain: polygonAmoy,
      transport: http(environment.PROVIDERS.polygonAmoy)

    });

    const nonce = await client.readContract({
      address: this.CTF_EXCHANGE_ADDRESS as `0x${string}`,
      abi: abi,
      functionName: 'nonces',
      args: [userAddress],
    });

    return nonce;
  }
}




